import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, NavLink } from "react-router-dom";
import LoggedLayout from '../../layouts/LoggedLayout';
import axios from 'axios';
import moment from 'moment';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageHeader from 'components/PageHeader';
import Paginator from 'components/Paginator';
import ThSortable from 'components/ThSortable';
import TrSkeleton from 'components/TrSkeleton';
import EmpoweredSelector from 'components/EmpoweredSelector';
import { default as StationsService } from 'services/Stations';
import { default as SuppliesService } from 'services/Supplies';
import { default as CompaniesService } from 'services/Companies';
import ConfigModal from './ConfigModal';

const Filters = styled.div`
	display: flex;
	justify-content: end;
	align-items: center;

	.filters-inputs {
	    display: flex;
		flex-direction: row;
		align-items: center;
		flex-wrap: nowrap;

		@media (max-width: 1200px) {
			& {
				display: flex !important;
				justify-content: flex-end;
				width: 100% !important;
			}
		}

		@media (min-width: 576px) and (max-width: 1200px) {
			& > *:nth-child(2) {
				min-width: 200px;
			}
		}

		@media (min-width: 768px) and (max-width: 991px) {
			#search-box {
				flex: 1;
			}
		}

		@media (max-width: 576px) {
			flex-direction: column;
			align-items: stretch;
			gap: 1rem;
			width: 100%;
			max-width: 100%;
			box-sizing: border-box;

			& > * {
				width: 100%;
				max-width: 100%;
				min-width: 0;
				box-sizing: border-box;
			}

			#search-box {
				width: 100%;
				max-width: 100%;
				min-width: 0;
				box-sizing: border-box;
			}
		}

		@media (max-width: 374px) {
			& {
				margin: auto;
			}
		}
	}

	.filter-buttons{
		width: auto !important;

		& > * {
			@media (min-width: 1201px) {
				height: 100%;
			}

			@media (max-width: 576px) {
				width: 100%;
				text-align: center;
			}
		}

		@media (max-width: 1200px) {
			margin-right: 0.5rem;
		}

		@media (max-width: 991px) {
			width: calc(100% - 1.5rem) !important;
		}

		@media (max-width: 767px) {
			width: calc(100% - 2rem);
		}

		@media (max-width: 576px) {
			display: flex !important;
			flex-direction: column !important;
			flex-wrap: nowrap;
			align-items: stretch !important;
			width: 100% !important;
		}
	}
	
	@media (max-width: 1200px) {
		justify-content: center;
		flex-direction: column;
		align-items: flex-end;

		& > *,
		.empowered-selector {
			width: 100% !important;
			display: block !important;
		}

		& > * {
			margin-bottom: 10px;
		}
	}

	@media (max-width: 991px) {
		display: grid;
		grid-template-columns: 1fr;
		grid-auto-rows: auto;
		gap: 10px;
		align-items: stretch;

		& > * {
			width: 100% !important;
			margin-bottom: 0 !important;
		}
	}

	@media (max-width: 767px) {
		grid-template-columns: 1fr;
	}
`;

const EmpoweredSelectorWrapper = styled.div` 
	margin-right: 10px;

	.empowered-selector {
		width: 200px;
		
		.empowered-selector_label {
			height: 28px;
			white-space: nowrap;
			overflow: hidden;

			&:hover {
				background: white;
			}

			.remove-icon {
				border-radius: 50%;
			}
		}
	}
`;

const HeaderCompanyEmpoweredSelector = styled.div`
	position: relative;
	padding-left: 10px;
	padding-right: 10px;
	width: 200px;
	border-right: 1px solid var(--bs-gray-500);
	
	.empowered-selector {

		.empowered-selector_label {
			border: 0;
			background: white;
		}
	}
`;

const HeaderButtonsTab = styled.div` 
	display: flex;
	flex-direction: row;
	width: 100%;
	height: 100%;

	a {
		display: flex;
		text-decoration: none;
		padding: 0px 10px;
		width: 50%;
		height: 100%;
		align-items: center;
		justify-content: center;
		text-transform: uppercase;

		&.active {
			background: var(--plenoil-blue);
			color: white;
		}
	}
`;

let axiosCancelToken = null;

export default function Supplies(props) {
	const type = props.type;

	const [queryParams] = useSearchParams();

	let [planning, setPlanning] = useState([]);
	let [sortDirection, setSortDirection] = useState('desc');
	let [sortField, setSortField] = useState('next_planning');
	let [station, setStation] = useState(null);
	let [company, setCompany] = useState(null);
	let [skeletonRows, setSkeletonRows] = useState(5);
	let [page, _setPage] = useState(queryParams.get('page') ?? undefined);
	let setPage = (page) => {
		setPlanning({...planning, data: undefined});
		_setPage(page);
	}
	let [editingNextPlanning, _setEditingNextPlanning] = useState([]);
	let setEditingNextPlanning = (arr) => {
		_setEditingNextPlanning(arr);
		editingRef.current = arr.length > 0 ? true: false;
	};
	let editingRef = useRef(false);
	let [globalConfigModalOpened, setGlobalConfigModalOpened] = useState(false);
	let [stationConfigModalOpened, setStationConfigModalOpened] = useState(null);

	const sortTableClick = (field) => {
		if ( !field ) return;
		if ( field === sortField ) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		setSortField(field);
	};	

	const loadStations = async (input, callback, args) => {
		let stations = await StationsService.getList({no_paginate: true, search: input, company_code: args.company_code});

		let formatted = stations.map((el) => {
			return {
				value: el, 
				label: el.name
			};
		});

		callback(formatted);
	}

	const loadCompanies = async (input, callback) => {
		let companies = await CompaniesService.list({no_paginate: true, search: input});

		let formatted = companies.map((el) => {
			return {
				value: el, 
				label: el.name
			};
		});

		callback(formatted);
	}

	const openChat = (e, station_id) => {
		e.stopPropagation();

		const event = new CustomEvent('open-chat', {detail: {stationId: station_id, channel: 'supplies'}});
		document.dispatchEvent(event);
	}

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();

		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

	useEffect(() => {
		let getPlanning = async () => {
			let planning = await SuppliesService.getPlanning({
				page: page,
				sort: sortField,
				direction: sortDirection,
				station_id: station?.id,
				company_id: company?.id,
				type: type
			});
			setSkeletonRows(planning?.data?.length);
			setPlanning({...planning});
		}
		getPlanning();

		let reloadInterval = setInterval(() => {
			if ( editingRef.current ) return; // Prevent reload if editing

			getPlanning();
		}, 5000);

		return function cleanup() {
			clearInterval(reloadInterval);
		}
	}, [page, sortField, sortDirection, station, company, type]);

	const toggleNextPlanningInput = (idx) => {
		let newEditingNextPlanning = [...editingNextPlanning];
		if ( newEditingNextPlanning.includes(idx) ) newEditingNextPlanning.splice(newEditingNextPlanning.indexOf(idx), 1);
		else newEditingNextPlanning.push(idx);
		setEditingNextPlanning(newEditingNextPlanning);
	}

	const setNextPlanning = (idx, date) => {
		let newPlanning = [...planning.data];
		newPlanning[idx].next_planning = date;
		setPlanning((prev) => ({...prev, data: newPlanning}));
	}

	const saveNextPlanning = async (idx) => {
		let stationId = planning.data[idx].id;
		let next_planning = planning.data[idx].next_planning;

		let result = await SuppliesService.setNextPlanning(stationId, type, next_planning);
		if ( !result.status ) {
			toast.error('Error al cambiar la fecha de planificación');
		}
 
		toggleNextPlanningInput(idx);
	}

	return (
		<LoggedLayout>
			<div className="col-md-12 mb-3">
				<PageHeader style={{overflowX: 'visible', overflowY: 'visible'}}>
					<span className="header-column header-title">Suministros Planificación</span>
					<HeaderCompanyEmpoweredSelector>
						<EmpoweredSelector
							load={(input, callback) => loadCompanies(input, callback)}
							onChange={(value) => setCompany(value)}
							timeout={200}
							label={company?.name}
							value={company?.id ?? null}
							placeholder="EMPRESA"
							noRemovable={false}
							noSearcheable={true}
						/>
					</HeaderCompanyEmpoweredSelector>
					<button className="btn btn-plenoil btn-plenoil-gray btn-sm p-0 px-1 ms-1 mb-1 mt-1 float-end d-flex flex-row flex-nowrap align-items-center gap-2" onClick={() => setGlobalConfigModalOpened(true)}><i className="bi bi-gear"></i> Planificación general</button>
					<span className="header-column header-icon-end">
						<i className="bi bi-printer text-plenoil-gray"></i>
					</span>
				</PageHeader>
			</div>

			<div className="col-md-12 mb-3">
				<div className="bg-white p-2 rounded shadow-sm">
					<div className="row">
						<div className="col-lg-2 align-content-center">
							<HeaderButtonsTab>
								<NavLink to="/supplies/planning/station" className="btn btn-sm btn-plenoil-outline-orange btn-plenoil p-0 px-2 me-2 text-nowrap col-4 col-lg-auto">estaciones</NavLink>
								<NavLink to="/supplies/planning/wash" className="btn btn-sm btn-plenoil-outline-orange btn-plenoil p-0 px-2 me-2 text-nowrap col-4 col-lg-auto">lavaderos</NavLink>
							</HeaderButtonsTab>
						</div>
						<Filters className="col-lg-10">
							<div className="filters-inputs">
								<EmpoweredSelectorWrapper>
									<EmpoweredSelector
										load={(input, callback, args) => loadStations(input, callback, args)}
										args={{company_code: company?.code}}
										onChange={(value, text) => setStation(value)}
										timeout={200}
										label={station?.name}
										value={station?.id ?? null}
										placeholder="Estación"
									/>
								</EmpoweredSelectorWrapper>
							</div>
						</Filters>
					</div>
				</div>
			</div>

			<div className="pt-2 col-md-12">
				<div className="px-0 col-lg pl-lg-0">
					<div className="row m-0 pb-2">
						<div className="col-12 px-0">
							<div className="h-100 p-4 background-color-blanco shadow rounded">
								<div className="table-responsive-rows">
									<div className="mvc-grid mvc-grid-header-mode">
										<div className="mvc-grid-table">
											<table>
												<thead>
													<tr>
														<ThSortable className="title_supplies-estacion" direction={sortDirection} active={sortField === 'station_name'} onClick={() => sortTableClick('station_name')}>Estación</ThSortable>
														<ThSortable className="title_supplies-proxima-solicitud" direction={sortDirection} active={sortField === 'next_planning'} onClick={() => sortTableClick('next_planning')}>Próxima solicitud</ThSortable>
														<ThSortable className="title_supplies-ultima-solicitud" direction={sortDirection} active={sortField === 'last_planning'} onClick={() => sortTableClick('last_planning')}>Última solicitud</ThSortable>
														<th></th>
														<th></th>
													</tr>
												</thead>
												<tbody>
													{ planning.data?.map((el, idx) => {
														return (
															<React.Fragment key={el.id}>
																<tr>
																	<td className="title_supplies-estacion">
																		<div className="d-flex justify-content-between">
																			<span>{el.station_name}</span>
																		</div>
																	</td>
																	<td className="title_supplies-proxima-solicitud d-flex flex-nowrap align-items-center gap-2">
																		{ !editingNextPlanning.includes(idx) ?
																			<>
																				{el.next_planning ? moment(el.next_planning).format('DD-MM-YYYY') : ''}
																				<button className="btn btn-sm btn-plenoil-outline-black d-flex flex-row flex-nowrap gap-2 align-items-center px-2 py-0" style={{ width: 26, height: 26, minWidth: 0, borderRadius: '1.5rem', paddingTop: '0 !important' }}>
																					<i className="bi bi-pencil" onClick={() => toggleNextPlanningInput(idx)}></i>
																				</button>
																			</>
																			:
																			<>
																				<input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={el.next_planning ? moment(el.next_planning).format('YYYY-MM-DD') : ''} onChange={(e) => setNextPlanning(idx, e.target.value)} />
																				<button className="btn btn-sm btn-plenoil-outline-green d-flex flex-row flex-nowrap gap-2 align-items-center px-2 py-0" style={{ width: 26, height: 26, minWidth: 0, borderRadius: '1.5rem', paddingTop: '0 !important' }} >
																					<i className="bi bi-check-circle-fill text-plenoil-green" onClick={() => saveNextPlanning(idx)}></i>
																				</button>
																			</>
																		}

																	</td>
																	<td className="title_supplies-ultima-solicitud">{el.last_planning ? moment(el.last_planning).format('DD-MM-YYYY') : ''}</td>
																	<td>
																		<button className="btn btn-sm btn-plenoil-outline-black d-flex flex-row flex-nowrap gap-2 align-items-center px-2 py-0" style={{ width: 26, height: 26, minWidth: 0, borderRadius: '1.5rem', paddingTop: '0 !important' }} onClick={(e) => setStationConfigModalOpened(el)}><i className="bi bi-gear"></i></button>
																	</td>
																	<td className={'chat ' + (!el.pending_chat_messages_count ? 'disabled' : '')}>
																		<button className="btn-plenoil-outline-blue" style={{
																			display: 'inline-flex',
																			alignItems: 'center',
																			justifyContent: 'center',
																			width: '28px',
																			height: '28px',
																			borderRadius: '50%',
																			border: '1px solid var(--plenoil-blue)'
																		}} onClick={(e) => openChat(e, el.id)}>
																			<i className={'bi bi-chat-right' + (el.pending_chat_messages_count ? '-fill' : '')}></i>
																		</button>
																	</td>
																</tr>
															</React.Fragment>
														)
													}) }

													{ planning.data && !planning.data.length && <tr><td colSpan="100%">No hay datos disponibles</td></tr> }

													{ planning.data === undefined && <TrSkeleton rows={skeletonRows} columns={5} /> }
												</tbody>
											</table>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="col-md-12 text-center mb-3">
				<div className="d-inline-block">
					<Paginator
						min={1}
						current={planning?.current_page}
						max={planning?.last_page}
						changeCallback={(page) => setPage(page)}
					/>
				</div>
			</div>
			{ globalConfigModalOpened &&
				<ConfigModal
					type={type}
					closeCallback={() => setGlobalConfigModalOpened(false)}
				/>
			}
			{ stationConfigModalOpened &&
				<ConfigModal
					type={type}
					station={stationConfigModalOpened}
					closeCallback={() => setStationConfigModalOpened(false)}
				/>
			}
		</LoggedLayout>
	);
}